import { getKey } from "@redotech/react-util/key";
import { InteractiveMapLocation } from "@redotech/redo-common-model-location-common/interactive-map-location";
import {
  IMPERIAL_DISTANCE_COUNTRIES,
  milesToKm,
} from "@redotech/redo-fulfillment-common/distance-conversion";
import { useTranslation } from "@redotech/redo-i18n";
import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Flex } from "../../flex";
import { LoadingRedoAnimation } from "../../loading-redo-animation";
import { Text } from "../../text";
import { RedoCheckboxCard } from "../checkbox/redo-checkbox-card";
import * as interactiveMap from "./interactive-map.module.css";

const GOOGLE_MAPS_PUBLIC_KEY = globalThis.GOOGLE_MAPS_PUBLIC_KEY ?? "";

/**
 * Modal body for a drop-off / in-store location search: shows a loading
 * animation while fetching, an error message, or the interactive map. Shared by
 * the customer portal and the merchant create-label modal.
 */
export const LocationsMapModalBody = memo(function LocationsMapModalBody({
  pending,
  error,
  locations,
  customerCountry,
  errorText,
  renderLocationExtra,
}: {
  pending: boolean;
  error: boolean;
  locations: InteractiveMapLocation[] | null;
  customerCountry?: string;
  errorText?: string;
  renderLocationExtra?: (location: InteractiveMapLocation) => ReactNode;
}) {
  const { t } = useTranslation();

  if (pending) {
    return <LoadingRedoAnimation />;
  }
  if (error) {
    return (
      <Text textColor="error">
        {errorText ?? t("Error loading locations", { context: "map" })}
      </Text>
    );
  }
  if (!locations) {
    return null;
  }

  return (
    <InteractiveMap
      customerCountry={customerCountry}
      key={getKey(locations)}
      locations={locations}
      renderLocationExtra={renderLocationExtra}
    />
  );
});

function formatDistance(
  distanceMiles: string | null | undefined,
  country: string,
): string | null {
  if (!distanceMiles) {
    return null;
  }
  const miles = parseFloat(distanceMiles);
  if (isNaN(miles)) {
    return null;
  }
  if (IMPERIAL_DISTANCE_COUNTRIES.has(country)) {
    return `${miles} miles`;
  }
  return `${milesToKm(miles).toFixed(1)} km`;
}

const LocationsList = memo(function LocationsList({
  locations,
  selectedIndex,
  selectLocation,
  customerCountry = "US",
  renderLocationExtra,
}: {
  locations: InteractiveMapLocation[];
  selectedIndex: number;
  selectLocation: (index: number) => void;
  customerCountry?: string;
  renderLocationExtra?: (location: InteractiveMapLocation) => ReactNode;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.children[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedIndex]);

  if (!locations.length) {
    return <div>{t("No locations found", { context: "map" })}</div>;
  }
  return (
    <div className={interactiveMap.locationsTextContainer} ref={ref}>
      <Flex dir="column" gap="2xl" p="xl">
        {locations.map((location, i) => (
          <RedoCheckboxCard
            checked={selectedIndex === i}
            key={i}
            onClick={() => selectLocation(i)}
            title={`${i + 1}. ${location.name}`}
          >
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" textColor="secondary">
                {(() => {
                  const dist = formatDistance(
                    location.distance,
                    customerCountry,
                  );
                  return dist ? (
                    <span>
                      {t("({{distance}})", {
                        context: "map distance",
                        distance: dist,
                      })}
                    </span>
                  ) : null;
                })()}
              </Text>
              <Text fontSize="sm" textColor="secondary">
                {location.address.address1}
              </Text>
              {location.address.address2 && (
                <Text fontSize="sm" textColor="secondary">
                  {location.address.address2}
                </Text>
              )}
              <Text fontSize="sm" textColor="secondary">
                {location.address.city}, {location.address.state || ""}{" "}
                {location.address.zip}
              </Text>
              {renderLocationExtra?.(location)}
            </Flex>
          </RedoCheckboxCard>
        ))}
      </Flex>
    </div>
  );
});

const LocationMap = memo(function LocationMap({
  locations,
  selectedIndex,
  selectLocation,
}: {
  locations: InteractiveMapLocation[];
  selectedIndex: number;
  selectLocation: (index: number) => void;
}) {
  const map = useMap();
  const positions = useMemo(() => {
    return locations.map((location) =>
      location.geo != null
        ? { lat: location.geo.latitude, lng: location.geo.longitude }
        : undefined,
    );
  }, [locations]);

  useEffect(() => {
    if (map && locations[selectedIndex]?.geo) {
      map.setCenter({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        lat: locations[selectedIndex].geo!.latitude,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        lng: locations[selectedIndex].geo!.longitude,
      });
    }
  }, [map, selectedIndex, locations]);

  if (locations[selectedIndex]?.geo == null) {
    return null;
  }

  return (
    <Flex className={interactiveMap.mapContainer}>
      <Map
        defaultCenter={positions[selectedIndex]}
        defaultZoom={12}
        fullscreenControl={false}
        mapId="37a5dbe51230f056"
        mapTypeControl={false}
        streetViewControl={false}
      >
        {positions.map(
          (position, i) =>
            position && (
              <AdvancedMarker
                key={i}
                onClick={() => selectLocation(i)}
                position={position}
                zIndex={i === selectedIndex ? 10_000 : i}
              >
                <Pin borderColor="black" scale={i === selectedIndex ? 1.25 : 1}>
                  <span
                    style={{ fontSize: i === selectedIndex ? "18px" : "14px" }}
                  >
                    {i + 1}
                  </span>
                </Pin>
              </AdvancedMarker>
            ),
        )}
      </Map>
    </Flex>
  );
});

export const InteractiveMap = memo(function InteractiveMap({
  locations,
  customerCountry,
  "data-dd-action-name": actionName,
  renderLocationExtra,
}: {
  "data-dd-action-name"?: string;
  locations: InteractiveMapLocation[];
  customerCountry?: string;
  renderLocationExtra?: (location: InteractiveMapLocation) => ReactNode;
}) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  if (!locations.length) {
    return <div>{t("No locations found", { context: "map" })}</div>;
  }

  return (
    <div
      className={interactiveMap.locationsContainer}
      data-dd-action-name={actionName}
    >
      <LocationsList
        customerCountry={customerCountry}
        locations={locations}
        renderLocationExtra={renderLocationExtra}
        selectedIndex={selectedIndex}
        selectLocation={setSelectedIndex}
      />
      <APIProvider apiKey={GOOGLE_MAPS_PUBLIC_KEY}>
        <LocationMap
          locations={locations}
          selectedIndex={selectedIndex}
          selectLocation={setSelectedIndex}
        />
      </APIProvider>
    </div>
  );
});
